/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';
import type { Interpolation } from '@emotion/react';

import * as React from 'react';

import { getUrl, onClickNavUrl } from '@coursera-web/cds-labs';

import config from 'js/app/config';
import useRouter from 'js/lib/useRouter';

import { Button } from '@coursera/cds-core';
import type { Theme } from '@coursera/cds-core';
import type { ButtonName } from '@coursera/event-pulse-types';
import { useTracker } from '@coursera/event-pulse/react';

import { NativeDataContext } from 'bundles/ent-website/components/NativeDataContext';
import type { NativeDataRegistryEntry } from 'bundles/ent-website/components/NativeDataContext';
import type { LinkIndividualLink } from 'bundles/ent-website/contentful/core/Navigation';
import { eventingTransform } from 'bundles/ent-website/utils/eventingTransform';
import { TrackedA } from 'bundles/page/components/TrackedLink2';

import _t from 'i18n!nls/ent-website';

export const PRIORITY_CTAS = {
  C4T: 'C4T',
  DEFAULT: 'DEFAULT',
} as const;

type PriorityCtaType = keyof typeof PRIORITY_CTAS;

// Interface for promo data structure
interface PromoData extends NativeDataRegistryEntry {
  promotion?: boolean;
  promoCode?: string;
}

// Custom hook for CTA configuration logic
function useCtaConfig(
  priorityCtaOverride: PriorityCtaType,
  primaryCta: LinkIndividualLink | undefined | null,
  href: string
) {
  const { getNativeData } = React.useContext(NativeDataContext);

  return React.useMemo(() => {
    if (priorityCtaOverride === PRIORITY_CTAS.C4T) {
      const PURCHASE_URL = `${config.url.base}purchase/plan`;
      const promo = getNativeData<PromoData>('promo');
      const params: Record<string, string> = {};
      if (promo?.promotion && promo.promoCode) {
        params.promoCode = promo.promoCode;
      }

      return {
        url: PURCHASE_URL,
        params,
        text: _t('Buy Now'),
        trackingName: eventingTransform('navigation_priority_c4t_cta'),
        eventName: 'ent_website_teams_buy_now' as ButtonName,
        linkName: _t('Buy Now'),
        shouldUseNavClick: true,
      };
    } else {
      const { ctaTarget, ctaText, shouldOpenInNewWindow } = primaryCta ?? {};
      return {
        url: ctaTarget || href,
        params: {},
        text: ctaText ?? _t('Contact Sales'),
        trackingName: eventingTransform('navigation_priority_cta'),
        eventName: 'ent_website_contact_sales' as ButtonName,
        linkName: ctaText ?? _t('Contact Sales'),
        shouldUseNavClick: false,
        shouldOpenInNewWindow,
      };
    }
  }, [priorityCtaOverride, primaryCta, href, getNativeData]);
}

type PriorityCtaProps = {
  primaryCta: LinkIndividualLink | undefined | null;
  href: string;
  priorityCtaOverride: PriorityCtaType;
};

/**
 * PriorityCtaOverride component renders a priority CTA button based on the specified type.
 *
 * @param priorityCtaOverride - The type of CTA to render (C4T or DEFAULT)
 * @param primaryCta - The primary CTA configuration from Contentful
 * @param href - Fallback URL for non-data-driven headers
 *
 * Note: The href prop is required for a fallback value for non data driven headers.
 * This will ideally be removed in the near future after experiments such as
 * the c4b promo and the c4t link are concluded and folded into our header.
 */
function PriorityCtaOverride({ priorityCtaOverride, primaryCta, href }: PriorityCtaProps) {
  const router = useRouter();
  const track = useTracker();

  const styles: Record<string, Interpolation<Theme>> = {
    button: css`
      display: inline-block;
      margin-inline-start: var(--cds-spacing-200);
    `,
  };

  const ctaConfig = useCtaConfig(priorityCtaOverride, primaryCta, href);

  const handleCtaClick = React.useCallback(
    (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
      track('click_button', {
        button: {
          name: ctaConfig.eventName,
          linkName: ctaConfig.linkName,
          linkURL: getUrl(router, ctaConfig.url, ctaConfig.params),
        },
      });

      // Done for UTM preservation
      if (ctaConfig.shouldUseNavClick) {
        onClickNavUrl(router, ctaConfig.url, ctaConfig.params)(event);
      }
    },
    [track, ctaConfig, router]
  );

  // Return null for unrecognized priorityCtaOverride values
  if (!Object.values(PRIORITY_CTAS).includes(priorityCtaOverride)) {
    return null;
  }

  const buttonHref = getUrl(router, ctaConfig.url, ctaConfig.params);

  return (
    <div css={styles.button}>
      <Button
        data-testid="ew_header_cta"
        onClick={handleCtaClick}
        trackingName={ctaConfig.trackingName}
        component={TrackedA}
        variant="primary"
        target={ctaConfig.shouldOpenInNewWindow ? '_blank' : undefined}
        href={buttonHref}
      >
        {ctaConfig.text}
      </Button>
    </div>
  );
}

export default PriorityCtaOverride;
